import clsx from 'clsx'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Grid,
  Modal,
  Link,
  Paper,
  Tooltip,
  Typography,
  Alert,
  IconButton,
} from '@mui/material'
import ContactForm from './ContactForm'
import React, { useRef, useEffect, useState } from 'react'
import { WEB_VIDEOS } from '../../../common/constants'
import { ReactComponent as WarningCircleFill } from '../../../assets/icons/warning-circle-fill.svg'
import { ReactComponent as Windows } from '../../../assets/icons/windows.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/x.svg'

import useTranslations from '../../../i18n/useTranslations'

import useStyles from './styles'
import { useUserContext } from '../../../auth/UserProvider'

// const openUserManualPdf = async () => {
//   window.open(
//     process.env.REACT_APP_TRANSFER_URL +
//       '/IT/area_cliente/Manual_usuario_digital_CTT_Express.pdf',
//     '_blank',
//   )
// }

const openUserManualPdf = async () => {
  const document = await fetch(
    process.env.PUBLIC_URL + '/documents/Manual usuario CTT Express.pdf',
  )
  const blob = await document.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

const VideoPreview = ({ video }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    // Establece el tiempo inicial del video cuando se cargan los metadatos
    const setInitialTime = () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 7
      }
    }

    if (videoRef.current) {
      videoRef.current.addEventListener('loadedmetadata', setInitialTime)
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', setInitialTime)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play()
    }
  }

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 7
    }
  }

  return (
    <video
      ref={videoRef}
      src={video.url}
      alt={`Previsualización de ${video.name}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      muted
      loop
      style={{ width: '100%', height: 'auto' }}
      preload="metadata" // Pre-carga los metadatos para establecer currentTime
    />
  )
}

const Help = ({ emailContact }) => {
  const [isVideosModalOpen, setVideosModalOpen] = useState(false)
  const classes = useStyles()
  const t = useTranslations()
  const user = useUserContext()

  const openVideosModal = () => {
    setVideosModalOpen(true)
  }

  const closeVideosModal = () => {
    setVideosModalOpen(false)
  }

  return (
    <Box component="section">
      <Typography variant="h1" className={classes.title}>
        {t('help:contact-support')}
      </Typography>
      <Typography className={classes.subtitle}>
        {t('help:solve-doubts')}
      </Typography>
      <Paper>
        <Grid container>
          <Grid item xs={12} md={6}>
            <Box className={classes.content}>
              <ContactForm
                name={user.webUserName}
                email={user.webUserEmail}
                phone={user.webUserPhone}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className={clsx(classes.content, classes.contentRight)}>
              <Typography className={classes.infoTeamViewer}>
                {t('help:team-viewer-info', {
                  link: text => (
                    <Link href="https://www.teamviewer.com/es/" target="_blank">
                      {text}
                    </Link>
                  ),
                })}
              </Typography>
              <Button
                className={classes.downloadTeams}
                component={Link}
                underline="none"
                href="https://download.teamviewer.com/download/TeamViewerQS.exe"
                size="small"
                startIcon={<Windows />}
              >
                {t('help:team-viewer-download')}
              </Button>
              <Button
                className={classes.downloadTeams}
                component={Link}
                underline="none"
                href="https://download.teamviewer.com/download/TeamViewerQS.dmg"
                size="small"
                startIcon={<Windows />}
              >
                {t('help:team-viewer-download-mac')}
              </Button>
              <Button
                className={classes.downloadTeams}
                component={Link}
                underline="none"
                href="https://download.anydesk.com/AnyDesk.exe"
                size="small"
                startIcon={<Windows />}
              >
                {t('help:any-desk-download')}
              </Button>
              <Button
                className={classes.downloadTeams}
                component={Link}
                underline="none"
                href="https://download.anydesk.com/anydesk.dmg"
                size="small"
                startIcon={<Windows />}
              >
                {t('help:any-desk-download-mac')}
              </Button>
              <Box className={classes.remote}>
                <Tooltip
                  placement="right"
                  classes={{
                    popper: classes.remoteTooltip,
                  }}
                  title={
                    <Typography variant="body2">
                      {t('help:remote-control-info', {
                        link: text => (
                          <Link
                            href="https://www.boe.es/eli/es/lo/2018/12/05/3/con"
                            target="_blank"
                            className={classes.remoteTooltipLink}
                          >
                            <strong>{text}</strong>
                          </Link>
                        ),
                      })}
                    </Typography>
                  }
                >
                  <Link className={classes.remoteLink}>
                    {t('help:what-is-remote-control')}
                    <WarningCircleFill className={classes.remoteIcon} />
                  </Link>
                </Tooltip>
              </Box>
              <Typography
                variant="h3"
                className={clsx('secondaryTitle', classes.contentTitle)}
              >
                {t('help:minimum-system-requirements')}
              </Typography>
              <Typography
                variant="body2"
                className={classes.infoRequirements}
                component="p"
              >
                {t('help:browsers', {
                  strong: text => <strong>{text}</strong>,
                  p: text => <p>{text}</p>,
                })}
              </Typography>
              <Typography
                variant="h2"
                className={clsx(
                  'secondaryTitle',
                  'decorator',
                  classes.contentTitle,
                )}
              >
                {t('help:user-manual')}
              </Typography>
              <Alert severity="info" className={classes.alert}>
                <Typography variant="h4" className={classes.downloadFormTitle}>
                  {t('help:user-manual-info')}
                </Typography>
                <Box className={classes.helpBox}>
                  <Button
                    className={classes.manualButton}
                    classes={{ root: 'mini' }}
                    onClick={openUserManualPdf}
                  >
                    {t('help:download-user-manual')}
                  </Button>
                  <Button classes={{ root: 'mini' }} onClick={openVideosModal}>
                    {t('help:see-videos')}
                  </Button>

                  <Modal open={isVideosModalOpen} onClose={closeVideosModal}>
                    <Box className={classes.modalStyle}>
                      <IconButton
                        aria-label="close"
                        onClick={closeVideosModal}
                        style={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                      <Typography
                        variant="h1"
                        gutterBottom
                        style={{ marginTop: '30px', marginLeft: '20px' }}
                      >
                        {t('help:videos')}
                      </Typography>
                      <Typography
                        variant="body1"
                        gutterBottom
                        style={{ marginLeft: '20px' }}
                      >
                        {t('help:videos-text')}
                      </Typography>
                      <Grid
                        container
                        className={classes.gridContainer}
                        spacing={2}
                      >
                        {WEB_VIDEOS.map((video, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            className={classes.gridItem}
                            key={index}
                          >
                            <Link
                              href={video.url}
                              target="_blank"
                              style={{ textDecoration: 'none' }}
                            >
                              <VideoPreview video={video} />
                              <Typography variant="body1" color="textPrimary">
                                {video.name}
                              </Typography>
                            </Link>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Modal>
                </Box>
              </Alert>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

Help.propTypes = {
  emailContact: PropTypes.string.isRequired,
}

export default Help
